import { GetAvailableOrderFulfillmentResponse } from '@/types';
import { getValidTimeslotForDate } from '@/experiences/sales-portal/blocs/utils/fulfillment-utils';
import { OrderAvailability } from '@domain/fulfillment-settings/blocs/order-availability';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';
import { useCallback } from 'react';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useSelectedFulfillmentStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';

export interface UseResetFulfillmentOnOrderSubmissionParams {
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  chatUUID: string | null | undefined;
  restaurantUUID: string;
  supplierUUID: string;
  timezones: TimeZoneMap;
}

/**
 * Returns a callback that resets the restaurant's persisted fulfillment
 * selection to the next default delivery day after order submission, gated on
 * the `reset_fulfillment_on_order_submission` dynamic config (chat-level
 * override, supplier-level fallback).
 */
export const useResetFulfillmentOnOrderSubmission = ({
  availableFulfillments,
  chatUUID,
  restaurantUUID,
  supplierUUID,
  timezones,
}: UseResetFulfillmentOnOrderSubmissionParams) => {
  const { value: isResetEnabled } = useChatSalesPortalDynamicConfigValue(
    supplierUUID,
    'reset_fulfillment_on_order_submission',
    chatUUID,
  );

  const resetFulfillmentOnOrderSubmission = useCallback(() => {
    const firstFulfillment = availableFulfillments[0];
    if (!isResetEnabled || !firstFulfillment) {
      return;
    }

    const defaultAvailability = new OrderAvailability(
      availableFulfillments,
      timezones,
      null,
    );
    const nextDate = defaultAvailability.nextFulfillmentDayMoment;
    useSelectedFulfillmentStore
      .getState()
      .saveSelectedFulfillment(restaurantUUID, {
        date: nextDate.toISOString(),
        fulfillmentSettingsUUID:
          defaultAvailability.selectedFulfillmentSettingsUUID,
        timeslot: getValidTimeslotForDate(firstFulfillment, nextDate, null),
        type: defaultAvailability.fulfillmentType,
      });
  }, [availableFulfillments, isResetEnabled, restaurantUUID, timezones]);

  return { resetFulfillmentOnOrderSubmission };
};
